import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  Pickaxe, TrendingUp, TrendingDown, Truck, Mountain, BarChart3,
  ArrowUpRight, ArrowDownRight, Minus, AlertTriangle, Target, ChevronRight,
  Crosshair, Shield, Layers, DollarSign, Clock, AlertCircle, CheckCircle, FileText, Info
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, ComposedChart, Line
} from 'recharts';
import PageHeader from '../../components/PageHeader';
import KPICardOperations from '../../components/KPICardOperations';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import RealVsBudgetTrajectory from '../../components/RealVsBudgetTrajectory';

// Import centralized data
import { mineAccessData, mineAccessTrajectoryKpis } from '../../data/mock/operationsData';

// Icon mapping for keyMessages
const iconMap = { Target, Clock, AlertTriangle, Layers, Shield, TrendingUp, TrendingDown, BarChart3, Truck, Mountain, DollarSign, AlertCircle, CheckCircle, FileText, Info, Crosshair, Pickaxe };

// Process keyMessages to add icon components from iconKey
const processKeyMessages = (messages) => {
  if (!messages) return [];
  return messages.map(msg => ({
    ...msg,
    icon: iconMap[msg.iconKey] || Target,
  }));
};

// Use centralized data with processed icons
const trajectoryKpis = mineAccessTrajectoryKpis;
const mineAccessDataProcessed = {
  2026: {
    ...mineAccessData[2026],
    keyMessages: processKeyMessages(mineAccessData[2026].keyMessages),
  },
  2025: {
    ...mineAccessData[2025],
    keyMessages: processKeyMessages(mineAccessData[2025].keyMessages),
  },
};

// Status Badge Component
const StatusBadge = ({ status, language }) => {
  const configs = {
    'conforme': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Conforme', en: 'On Track' } },
    'acompanhamento': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Acompanhamento', en: 'Monitoring' } },
    'pressao': { bg: 'bg-rose-100', text: 'text-rose-700', label: { pt: 'Pressão', en: 'Pressure' } },
    'critico': { bg: 'bg-rose-100', text: 'text-rose-700', label: { pt: 'Crítico', en: 'Critical' } },
  };
  const config = configs[status] || configs['conforme'];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-semibold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// Severity Badge Component
const SeverityBadge = ({ severity, language }) => {
  const configs = {
    'high': { bg: 'bg-rose-100', text: 'text-rose-700', label: { pt: 'Relevante', en: 'Relevant' } },
    'medium': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Moderado', en: 'Moderate' } },
    'low': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Limitado', en: 'Limited' } },
  };
  const config = configs[severity] || configs['medium'];
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// Risk Badge Component
const RiskBadge = ({ risk, language }) => {
  const configs = {
    'high': { bg: 'bg-rose-100', text: 'text-rose-700', label: { pt: 'Alto', en: 'High' } },
    'medium': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Médio', en: 'Medium' } },
    'low': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Baixo', en: 'Low' } },
  };
  const config = configs[risk] || configs['medium'];
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

const MineAccess = ({ year, setActiveSection, flowMap }) => {
  const { language } = useLanguage();
  const is2026 = year === 2026;
  const data = is2026 ? mineAccessDataProcessed[2026] : mineAccessDataProcessed[2025];

  // Dados para o painel Real vs Trajectória do Orçamento - Múltiplos KPIs
  const trajectoryKpisYear = is2026 ? trajectoryKpis[2026] : trajectoryKpis[2025];

  // Preparar dados para export PDF
  const pageDataForExport = {
    kpis: data.kpis,
    kpiLabels: {
      minerioExtraido: language === 'pt' ? 'Minério Extraído' : 'Ore Extracted',
      esterilRemovido: language === 'pt' ? 'Estéril Removido' : 'Waste Removed',
      stripRatio: 'Strip Ratio',
      perfuracaoProducao: language === 'pt' ? 'Perfuração de Produção' : 'Production Drilling',
    },
    keyMessages: data.keyMessages,
    ponteFinanceira: data.ponteFinanceira,
    watchlist: data.watchlist,
  };

  return (
    <div className="space-y-8" data-testid="mine-access">
      {/* ============ PAGE HEADER ============ */}
      <PageHeader
        title={language === 'pt' ? 'Mina e Acesso ao Minério' : 'Mine Access & Ore'}
        period={data.period}
        subtitle={language === 'pt' 
          ? `vs Orçamento ${data.period} e comparativo com ${is2026 ? '1T25' : '2024'}`
          : `vs Budget ${data.period} and comparison with ${is2026 ? 'Q1 25' : '2024'}`}
        language={language}
        flowMap={flowMap}
        pageData={pageDataForExport}
      />

      {/* ============ MENSAGENS-CHAVE - McKinsey Premium Compact ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#112240] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-0.5 h-4 bg-gold-500 rounded-full"></div>
          <h3 className="text-white/90 font-medium text-xs uppercase tracking-[0.15em]">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {data.keyMessages.map((msg, idx) => {
            const MsgIcon = msg.icon;
            return (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/8 transition-colors">
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.iconBg}`}>
                    <MsgIcon className={`w-4 h-4 ${msg.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-[10px] mb-1.5 uppercase tracking-wider ${msg.titleColor}`}>
                      {msg.title[language]}
                    </h4>
                    <p className="text-slate-300/90 text-[11px] leading-relaxed">{msg.text[language]}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ KPIs (4 KPIs oficiais) - Estéril Removido como KPI Âncora ============ */}
      <div className="grid grid-cols-4 gap-4">
        {/* KPI ÂNCORA: Estéril Removido - Maior destaque visual */}
        <div className="relative">
          <div className="absolute -left-1 top-0 bottom-0 w-1 bg-gold-500 rounded-full" />
          <KPICardOperations 
            label={language === 'pt' ? 'Estéril Removido' : 'Waste Removed'}
            value={data.kpis.esterilRemovido.actual >= 1000000 ? (data.kpis.esterilRemovido.actual / 1000).toFixed(0) + 'K' : data.kpis.esterilRemovido.actual >= 1000 ? (data.kpis.esterilRemovido.actual / 1000).toFixed(0) + 'K' : data.kpis.esterilRemovido.actual.toLocaleString()}
            unit={data.kpis.esterilRemovido.unit}
            vsBudget={data.kpis.esterilRemovido.variance}
            vsPrior={data.kpis.esterilRemovido.yoyVariance}
            budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
            yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
            isAnchor={true}
            language={language}
            tooltipData={{
              title: language === 'pt' ? 'Estéril Removido' : 'Waste Removed',
              definition: language === 'pt' 
                ? 'Volume de material estéril (rocha sem valor económico) removido para expor o minério.'
                : 'Volume of waste material (rock with no economic value) removed to expose ore.',
              relevance: language === 'pt'
                ? 'Indicador-chave de acesso ao minério. Atrasos comprometem a disponibilidade futura de frentes de lavra.'
                : 'Key ore access indicator. Delays compromise future availability of mining fronts.',
              currentReading: language === 'pt'
                ? `Remoção de ${(data.kpis.esterilRemovido.actual/1000).toFixed(0)}K m³ no período. Strip ratio actual em ${data.kpis.stripRatio.actual}:1.`
                : `Removal of ${(data.kpis.esterilRemovido.actual/1000).toFixed(0)}K m³ in period. Current strip ratio at ${data.kpis.stripRatio.actual}:1.`,
              drillDown: [
                { label: 'Mina 3', value: '312K m³', variance: -4.2 },
                { label: 'Mina 4', value: '156K m³', variance: -8.1 },
                { label: 'Calonda', value: '52K m³', variance: -6.5 },
              ]
            }}
          />
        </div>
        {/* KPIs de Suporte */}
        <KPICardOperations 
          label={language === 'pt' ? 'Minério Extraído' : 'Ore Extracted'}
          value={data.kpis.minerioExtraido.actual >= 1000000 ? (data.kpis.minerioExtraido.actual / 1000).toFixed(0) + 'K' : data.kpis.minerioExtraido.actual >= 1000 ? (data.kpis.minerioExtraido.actual / 1000).toFixed(0) + 'K' : data.kpis.minerioExtraido.actual.toLocaleString()}
          unit={data.kpis.minerioExtraido.unit}
          vsBudget={data.kpis.minerioExtraido.variance}
          vsPrior={data.kpis.minerioExtraido.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Minério Extraído' : 'Ore Extracted',
            definition: language === 'pt' 
              ? 'Volume de material mineralizado extraído das frentes de lavra no período.'
              : 'Volume of mineralized material extracted from mining fronts in the period.',
            relevance: language === 'pt'
              ? 'Determina o ritmo de alimentação potencial da planta. Desvios afectam directamente o stockpile e a produção.'
              : 'Determines potential plant feed rate. Deviations directly affect stockpile and production.',
            currentReading: language === 'pt'
              ? `Extracção de ${(data.kpis.minerioExtraido.actual/1000).toFixed(0)}K m³ vs ${((data.kpis.minerioExtraido.actual/1000)/(1+data.kpis.minerioExtraido.variance/100)).toFixed(0)}K m³ planeados.`
              : `Extraction of ${(data.kpis.minerioExtraido.actual/1000).toFixed(0)}K m³ vs ${((data.kpis.minerioExtraido.actual/1000)/(1+data.kpis.minerioExtraido.variance/100)).toFixed(0)}K m³ planned.`,
            drillDown: [
              { label: 'Mina 3', value: '171K m³', variance: -3.5 },
              { label: 'Mina 4', value: '85K m³', variance: -7.2 },
              { label: 'Calonda', value: '29K m³', variance: -10.0 },
            ]
          }}
        />
        <KPICardOperations 
          label="Strip Ratio"
          value={data.kpis.stripRatio.actual}
          unit={data.kpis.stripRatio.unit}
          vsBudget={data.kpis.stripRatio.variance}
          vsPrior={data.kpis.stripRatio.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          inverse={true}
          language={language}
          tooltipData={{
            title: 'Strip Ratio',
            definition: language === 'pt' 
              ? 'Rácio entre volume de estéril removido e minério extraído (m³ estéril / m³ minério).'
              : 'Ratio between waste removed and ore extracted volume (m³ waste / m³ ore).',
            relevance: language === 'pt'
              ? 'Mede a eficiência de acesso. Valores acima do plano indicam maior custo unitário de movimentação.'
              : 'Measures access efficiency. Values above plan indicate higher unit movement cost.',
            currentReading: language === 'pt'
              ? `Rácio de ${data.kpis.stripRatio.actual}:1 alinhado com o plano de ${(data.kpis.stripRatio.actual/(1+data.kpis.stripRatio.variance/100)).toFixed(2)}:1.`
              : `Ratio of ${data.kpis.stripRatio.actual}:1 aligned with plan of ${(data.kpis.stripRatio.actual/(1+data.kpis.stripRatio.variance/100)).toFixed(2)}:1.`,
            drillDown: [
              { label: 'Mina 3', value: '1.82:1', variance: -0.5 },
              { label: 'Mina 4', value: '1.84:1', variance: 1.1 },
              { label: 'Calonda', value: '1.79:1', variance: -2.2 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Perfuração de Produção' : 'Production Drilling'}
          value={data.kpis.perfuracaoProducao.actual >= 1000 ? (data.kpis.perfuracaoProducao.actual / 1000).toFixed(1) + 'K' : data.kpis.perfuracaoProducao.actual.toLocaleString()}
          unit={data.kpis.perfuracaoProducao.unit}
          vsBudget={data.kpis.perfuracaoProducao.variance}
          vsPrior={data.kpis.perfuracaoProducao.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Perfuração de Produção' : 'Production Drilling',
            definition: language === 'pt' 
              ? 'Metros lineares de perfuração realizados para preparação de desmonte (blasting).'
              : 'Linear meters of drilling performed for blast preparation.',
            relevance: language === 'pt'
              ? 'Indicador avançado de prontidão operacional. Atrasos hoje impactam a extracção dos próximos períodos.'
              : 'Leading indicator of operational readiness. Delays today impact extraction in coming periods.',
            currentReading: language === 'pt'
              ? `Perfuração de ${(data.kpis.perfuracaoProducao.actual/1000).toFixed(1)}K m. Preparação para ${is2026 ? '2T26' : '1T26'} pode estar em risco.`
              : `Drilling of ${(data.kpis.perfuracaoProducao.actual/1000).toFixed(1)}K m. Preparation for ${is2026 ? 'Q2 26' : 'Q1 26'} may be at risk.`,
            drillDown: [
              { label: language === 'pt' ? 'Furos primários' : 'Primary holes', value: '8.2K m', variance: -12.5 },
              { label: language === 'pt' ? 'Furos secundários' : 'Secondary holes', value: '4.8K m', variance: -8.2 },
              { label: language === 'pt' ? 'Pré-corte' : 'Pre-split', value: '1.0K m', variance: -5.0 },
            ]
          }}
        />
      </div>

      {/* ============ REAL VS TRAJECTÓRIA DO ORÇAMENTO ============ */}
      <RealVsBudgetTrajectory 
        year={year}
        kpis={trajectoryKpisYear}
        defaultKpi="esterilRemovido"
      />

      {/* ============ SUMÁRIO EXECUTIVO - Formato original com blocos expandidos ============ */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Mina e acesso ao minério' : 'Mine and ore access'}
        language={language}
        maxHeight="500px"
        icon={Mountain}
        dataTestId="mine-access-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {is2026
              ? (language === 'pt' 
                  ? `Esta página acompanha se a operação está a criar acesso ao minério ao ritmo necessário para sustentar a produção e o orçamento. O foco não está apenas na extracção do período, mas também na preparação de bancos, prontidão operacional e risco futuro. O 1T 2026 apresenta sinais de alerta que merecem atenção da gerência: minério extraído -5% vs orçamento, estéril removido -5.5%, e perfuração de produção -10.7%. Embora o strip ratio esteja alinhado (1.82:1 vs 1.83:1 planeado), o atraso na perfuração indica risco sobre a preparação dos próximos períodos.`
                  : `This page tracks whether operations are creating ore access at the pace required to sustain production and budget. The focus is not just on period extraction, but also on bench preparation, operational readiness and future risk. Q1 2026 shows warning signs that deserve management attention: ore extracted -5% vs budget, waste removed -5.5%, and production drilling -10.7%. Although the strip ratio is aligned (1.82:1 vs 1.83:1 planned), the drilling delay indicates risk on preparation for coming periods.`)
              : (language === 'pt'
                  ? `O ano de 2025 revelou execução consistente na área de mina. O acesso ao minério foi criado ao ritmo necessário para sustentar a produção, com desvios marginais que não comprometeram a alimentação da planta. O minério extraído atingiu 98.3% do plano (1.18M m³ vs 1.20M m³ orçado), enquanto a remoção de estéril manteve o strip ratio exactamente no valor planeado de 1.83:1. A perfuração de produção ficou -3.4% abaixo do target, mas sem impacto material na prontidão operacional.`
                  : `2025 revealed consistent execution in the mine area. Ore access was created at the pace required to sustain production, with marginal deviations that did not compromise plant feed. Ore extracted reached 98.3% of plan (1.18M m³ vs 1.20M m³ budget), while waste removal kept the strip ratio exactly at the planned value of 1.83:1. Production drilling was -3.4% below target, but without material impact on operational readiness.`)
            }
          </p>
        </div>

        {/* Driver Sections - Collapsible Cards */}
        <div className="space-y-4">
          {/* Ritmo de Abertura e Desvios */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Pickaxe className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Ritmo de Abertura e Desvios' : 'Opening Pace and Deviations'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Análise do acesso ao minério no período' : 'Period ore access analysis'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {is2026
                ? (language === 'pt' 
                    ? `O ritmo de abertura da mina no 1T 2026 ficou abaixo do planeado em todas as frentes principais. O minério extraído totalizou 285K m³ contra 300K m³ orçamentados (-5%), enquanto o estéril removido atingiu 520K m³ vs 550K m³ planeados (-5.5%). A massa mineira total foi de 805K m³, representando um desvio de -5.3% face ao orçamento de 850K m³.`
                    : `Mine opening pace in Q1 2026 was below plan on all main fronts. Ore extracted totaled 285K m³ against 300K m³ budgeted (-5%), while waste removed reached 520K m³ vs 550K m³ planned (-5.5%). Total mined mass was 805K m³, representing a -5.3% deviation from the 850K m³ budget.`)
                : (language === 'pt'
                    ? `O ritmo de abertura da mina em 2025 foi consistente ao longo dos quatro trimestres. O minério extraído totalizou 1.18M m³ (98.3% do plano), enquanto o estéril removido atingiu 2.16M m³ (98.2% do plano). A massa mineira total foi de 3.34M m³, com desvio marginal de -1.8% face ao orçamento.`
                    : `Mine opening pace in 2025 was consistent throughout the four quarters. Ore extracted totaled 1.18M m³ (98.3% of plan), while waste removed reached 2.16M m³ (98.2% of plan). Total mined mass was 3.34M m³, with marginal deviation of -1.8% vs budget.`)
              }
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Desvios do Período' : 'Period Deviations'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${is2026 ? 'bg-rose-500' : 'bg-emerald-500'}`}></div>
                  {is2026 
                    ? (language === 'pt' ? 'Minério Extraído: 285K m³ vs 300K m³ orç. (-5.0%)' : 'Ore Extracted: 285K m³ vs 300K m³ bud. (-5.0%)')
                    : (language === 'pt' ? 'Minério Extraído: 1.18M m³ vs 1.20M m³ orç. (-1.7%)' : 'Ore Extracted: 1.18M m³ vs 1.20M m³ bud. (-1.7%)')
                  }
                </li>
                <li className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${is2026 ? 'bg-rose-500' : 'bg-emerald-500'}`}></div>
                  {is2026
                    ? (language === 'pt' ? 'Estéril Removido: 520K m³ vs 550K m³ orç. (-5.5%)' : 'Waste Removed: 520K m³ vs 550K m³ bud. (-5.5%)')
                    : (language === 'pt' ? 'Estéril Removido: 2.16M m³ vs 2.20M m³ orç. (-1.8%)' : 'Waste Removed: 2.16M m³ vs 2.20M m³ bud. (-1.8%)')
                  }
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {is2026
                    ? (language === 'pt' ? 'Strip Ratio: 1.82:1 vs 1.83:1 orç. (-0.5%)' : 'Strip Ratio: 1.82:1 vs 1.83:1 bud. (-0.5%)')
                    : (language === 'pt' ? 'Strip Ratio: 1.83:1 vs 1.83:1 orç. (0%)' : 'Strip Ratio: 1.83:1 vs 1.83:1 bud. (0%)')
                  }
                </li>
                <li className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${is2026 ? 'bg-rose-500' : 'bg-amber-500'}`}></div>
                  {is2026
                    ? (language === 'pt' ? 'Perfuração Produção: 12.5K m vs 14K m orç. (-10.7%)' : 'Production Drilling: 12.5K m vs 14K m bud. (-10.7%)')
                    : (language === 'pt' ? 'Perfuração Produção: 56K m vs 58K m orç. (-3.4%)' : 'Production Drilling: 56K m vs 58K m bud. (-3.4%)')
                  }
                </li>
              </ul>
            </div>
          </div>

          {/* Leitura de Risco Operacional */}
          <div className={`bg-gradient-to-r ${is2026 ? 'from-rose-50 to-slate-50 border-rose-100' : 'from-emerald-50 to-slate-50 border-emerald-100'} rounded-xl p-5 border`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl ${is2026 ? 'bg-rose-100' : 'bg-emerald-100'} flex items-center justify-center`}>
                {is2026 
                  ? <AlertTriangle className="w-5 h-5 text-rose-600" />
                  : <CheckCircle className="w-5 h-5 text-emerald-600" />
                }
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">
                  {is2026 
                    ? (language === 'pt' ? 'Leitura de Risco Operacional' : 'Operational Risk Reading')
                    : (language === 'pt' ? 'Diagnóstico de Execução' : 'Execution Diagnosis')
                  }
                </h4>
                <p className="text-xs text-slate-500">
                  {is2026
                    ? (language === 'pt' ? 'Implicações para períodos seguintes' : 'Implications for coming periods')
                    : (language === 'pt' ? 'O que o período revelou' : 'What the period revealed')
                  }
                </p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {is2026
                ? (language === 'pt' 
                    ? `O principal sinal de alerta está no atraso da perfuração de produção (-10.7%), que condiciona a preparação de bancos para os trimestres seguintes. Este desvio, combinado com a pressão sobre Mina 2 (M2), cria um cenário de risco moderado-alto para a sustentabilidade do plano de produção no 2T e 3T 2026.`
                    : `The main warning sign is in production drilling delay (-10.7%), which conditions bench preparation for coming quarters. This deviation, combined with pressure on Mine 2 (M2), creates a moderate-high risk scenario for production plan sustainability in Q2 and Q3 2026.`)
                : (language === 'pt'
                    ? `O período revelou execução robusta na criação de acesso ao minério. Nenhuma frente apresentou desvios críticos e a preparação para 2026 ficou assegurada. A perfuração ficou -3.4% abaixo do target, mas dentro da tolerância operacional sem impacto na prontidão.`
                    : `The period revealed robust execution in ore access creation. No front showed critical deviations and preparation for 2026 was ensured. Drilling was -3.4% below target, but within operational tolerance without impact on readiness.`)
              }
            </p>
            <p className="text-slate-600 text-xs leading-relaxed">
              {is2026
                ? (language === 'pt' 
                    ? `Para a gerência, a mensagem é clara: o desvio actual não compromete imediatamente a alimentação da planta (suportada pelo stockpile), mas reduz a margem de segurança operacional e aumenta a dependência de recuperação nos próximos meses.`
                    : `For management, the message is clear: current deviation does not immediately compromise plant feed (supported by stockpile), but reduces operational safety margin and increases dependency on recovery in coming months.`)
                : (language === 'pt'
                    ? `A execução de 2025 demonstrou que a operação física protegeu a previsibilidade financeira, mantendo custo unitário estável e evitando pressões inesperadas sobre caixa.`
                    : `2025 execution demonstrated that physical operations protected financial predictability, maintaining stable unit cost and avoiding unexpected cash pressures.`)
              }
            </p>
          </div>

          {/* Ponte Operação-Finanças */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Ponte Operação → Finanças' : 'Operations → Finance Bridge'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Tradução dos desvios em linguagem financeira' : 'Translation of deviations to financial language'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {is2026
                ? (language === 'pt' 
                    ? `Os desvios observados na mina têm implicações financeiras directas: menor volume de minério extraído pressiona o custo unitário de produção (diluição de custos fixos); atraso na perfuração pode forçar aceleração futura com custos adicionais; e a dependência crescente do stockpile reduz flexibilidade na gestão de blending e qualidade.`
                    : `Deviations observed in the mine have direct financial implications: lower ore volume extracted pressures unit production cost (fixed cost dilution); drilling delay may force future acceleration with additional costs; and increasing stockpile dependency reduces flexibility in blending and quality management.`)
                : (language === 'pt'
                    ? `A execução de 2025 demonstrou robustez que protegeu custo unitário e geração de caixa. Os desvios marginais não criaram dependência de stockpile nem pressão sobre flexibilidade de blending. O custo unitário de mina manteve-se estável e previsível.`
                    : `2025 execution demonstrated robustness that protected unit cost and cash generation. Marginal deviations did not create stockpile dependency or pressure on blending flexibility. Mine unit cost remained stable and predictable.`)
              }
            </p>
            <p className="text-slate-600 text-xs leading-relaxed">
              {is2026
                ? (language === 'pt' 
                    ? `Esta é uma página de ligação entre operação física e risco financeiro — os desvios aqui observados transformam-se em pressão sobre custo unitário, dependência de stockpile e incerteza na geração de caixa. A trajectória actual exige recuperação no 2T para proteger o orçamento anual.`
                    : `This is a bridge page between physical operation and financial risk — deviations observed here translate into pressure on unit cost, stockpile dependency and cash generation uncertainty. Current trajectory requires Q2 recovery to protect annual budget.`)
                : (language === 'pt'
                    ? `O ano de 2025 confirmou que a prontidão física na área de mina é um factor crítico de protecção da previsibilidade financeira. A disciplina de execução observada deve servir de referência para 2026.`
                    : `2025 confirmed that physical readiness in the mine area is a critical factor for protecting financial predictability. The execution discipline observed should serve as reference for 2026.`)
              }
            </p>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* ============ BLOCO 1: RITMO DE ABERTURA DA MINA - McKinsey Compact ============ */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Pickaxe className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Ritmo de Abertura da Mina' : 'Mine Opening Pace'}</h3>
            <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Minério, estéril, massa mineira e strip ratio' : 'Ore, waste, total mined and strip ratio'}</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5 text-[10px] text-primary/70 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
            <Target className="w-3 h-3" />
            <span className="font-medium">{language === 'pt' ? 'Pergunta-chave:' : 'Key question:'}</span>
            <span className="italic">{language === 'pt' ? 'Estamos a abrir a mina ao ritmo necessário?' : 'Are we opening at required pace?'}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-5">
          {/* Chart */}
          <div className="col-span-2">
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.ritmoAbertura.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(v) => `${v}K`} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                    labelStyle={{ color: '#C5A059' }}
                    itemStyle={{ color: '#fff' }}
                    formatter={(value, name) => [`${value}K m³`, name]}
                  />
                  <Bar yAxisId="left" dataKey="minerioReal" name={language === 'pt' ? 'Minério Real' : 'Actual Ore'} fill="#C5A059" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="left" dataKey="minerioOrc" name={language === 'pt' ? 'Minério Orç.' : 'Budget Ore'} fill="#C5A05950" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="esterilReal" name={language === 'pt' ? 'Estéril Real' : 'Actual Waste'} stroke="#0A192F" strokeWidth={2.5} dot={{ fill: '#0A192F', r: 4, strokeWidth: 0 }} />
                  <Line yAxisId="left" type="monotone" dataKey="esterilOrc" name={language === 'pt' ? 'Estéril Orç.' : 'Budget Waste'} stroke="#64748b" strokeWidth={2} strokeDasharray="6 3" dot={{ fill: '#64748b', r: 3, strokeWidth: 0 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            
            {/* Custom Legend - More visible */}
            <div className="flex flex-wrap justify-center gap-4 mt-3 pb-1">
              {/* Minério Section */}
              <div className="flex items-center gap-3 px-3 py-1.5 bg-gold-50 rounded-lg border border-gold-200">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-3 rounded-sm bg-gold-500"></div>
                  <span className="text-[10px] font-semibold text-gold-700">{language === 'pt' ? 'Minério Real' : 'Actual Ore'}</span>
                </div>
                <div className="w-px h-3 bg-gold-300"></div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-3 rounded-sm bg-gold-500/30 border border-gold-400/50"></div>
                  <span className="text-[10px] text-gold-600">{language === 'pt' ? 'Minério Orç.' : 'Budget Ore'}</span>
                </div>
              </div>
              
              {/* Estéril Section */}
              <div className="flex items-center gap-3 px-3 py-1.5 bg-slate-100 rounded-lg border border-slate-300">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-0.5 bg-primary rounded-full"></div>
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <span className="text-[10px] font-semibold text-primary">{language === 'pt' ? 'Estéril Real' : 'Actual Waste'}</span>
                </div>
                <div className="w-px h-3 bg-slate-400"></div>
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center">
                    <div className="w-1.5 h-0.5 bg-slate-500 rounded-full"></div>
                    <div className="w-1 h-0.5 bg-transparent"></div>
                    <div className="w-1.5 h-0.5 bg-slate-500 rounded-full"></div>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-slate-500"></div>
                  <span className="text-[10px] text-slate-600">{language === 'pt' ? 'Estéril Orç.' : 'Budget Waste'}</span>
                </div>
              </div>
            </div>
            
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
              {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção | Unidade: K m³' : 'Source: Monthly Production Reports | Unit: K m³'}
            </p>
          </div>
          
          {/* YTD Summary - Compact */}
          <div className="space-y-2">
            <h4 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              {language === 'pt' ? 'Acumulado Período' : 'Period YTD'}
            </h4>
            <div className="space-y-1.5">
              {Object.entries(data.ritmoAbertura.ytd).map(([key, val]) => (
                <div key={key} className="p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="text-[10px] text-slate-500 capitalize">
                      {key === 'minerio' ? (language === 'pt' ? 'Minério' : 'Ore') :
                       key === 'esteril' ? (language === 'pt' ? 'Estéril' : 'Waste') :
                       key === 'massaMineira' ? (language === 'pt' ? 'Massa Mineira' : 'Total Mined') :
                       'Strip Ratio'}
                    </span>
                    <span className={`text-[10px] font-bold ${val.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {val.variance > 0 ? '+' : ''}{val.variance}%
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-base font-bold font-mono text-slate-800">{val.real}</span>
                    <span className="text-[10px] text-slate-400">vs {val.orc} {val.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ============ BLOCO 2: ACESSO AO MINÉRIO POR CAVA/FRENTE - McKinsey Compact ============ */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Layers className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Acesso ao Minério por Cava / Frente' : 'Ore Access by Pit / Front'}</h3>
            <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Leitura qualitativa baseada em relatórios mensais' : 'Qualitative reading based on monthly reports'}</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5 text-[10px] text-primary/70 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
            <Target className="w-3 h-3" />
            <span className="font-medium">{language === 'pt' ? 'Pergunta-chave:' : 'Key question:'}</span>
            <span className="italic">{language === 'pt' ? 'Onde está o gargalo físico?' : 'Where is the bottleneck?'}</span>
          </div>
        </div>
        
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Cava / Frente' : 'Pit / Front'}</th>
                <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-28">Status</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Evidência do Mês' : 'Monthly Evidence'}</th>
                <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Leitura Executiva' : 'Executive Reading'}</th>
                <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-20">{language === 'pt' ? 'Risco' : 'Risk'}</th>
              </tr>
            </thead>
            <tbody>
              {data.acessoCava.map((cava, idx) => (
                <tr key={cava.id} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/30' : ''} hover:bg-slate-50/60 transition-colors`}>
                  <td className="py-2 px-3">
                    <span className="font-medium text-slate-700">{cava.name}</span>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <StatusBadge status={cava.status} language={language} />
                  </td>
                  <td className="py-2 px-3">
                    <span className="text-slate-600 text-[10px]">{cava.evidence[language]}</span>
                  </td>
                  <td className="py-2 px-3">
                    <span className="text-slate-600 text-[10px]">{cava.executiveReading[language]}</span>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <RiskBadge risk={cava.risk} language={language} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 mt-2.5 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção e POA 2026' : 'Source: Monthly Production Reports and POA 2026'}
        </p>
      </div>

      {/* ============ BLOCO 3: PERFURAÇÃO E PREPARAÇÃO DE BANCO - McKinsey Compact ============ */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Crosshair className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Perfuração e Preparação de Banco' : 'Drilling and Bench Preparation'}</h3>
            <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Plano vs real e leitura de prontidão' : 'Plan vs actual and readiness reading'}</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5 text-[10px] text-primary/70 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
            <Target className="w-3 h-3" />
            <span className="font-medium">{language === 'pt' ? 'Pergunta-chave:' : 'Key question:'}</span>
            <span className="italic">{language === 'pt' ? 'A mina está preparada para o ritmo seguinte?' : 'Is the mine prepared?'}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-5">
          {/* Chart */}
          <div className="col-span-2">
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.perfuracao.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                    labelStyle={{ color: '#C5A059' }}
                    formatter={(value) => `${value.toLocaleString()} m`}
                  />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="real" name={language === 'pt' ? 'Real (m)' : 'Actual (m)'} fill="#0A192F" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="plan" name={language === 'pt' ? 'Plano (m)' : 'Plan (m)'} fill="#0A192F40" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
              {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção' : 'Source: Monthly Production Reports'}
            </p>
          </div>
          
          {/* Readiness Commentary - Compact */}
          <div className="space-y-3">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  {language === 'pt' ? 'Acumulado' : 'YTD'}
                </span>
                <span className={`text-xs font-bold ${data.perfuracao.ytd.variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {data.perfuracao.ytd.variance > 0 ? '+' : ''}{data.perfuracao.ytd.variance}%
                </span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold font-mono text-slate-800">
                  {(data.perfuracao.ytd.real / 1000).toFixed(1)}K
                </span>
                <span className="text-[10px] text-slate-400">vs {(data.perfuracao.ytd.plan / 1000).toFixed(1)}K m</span>
              </div>
            </div>
            
            <div className={`p-3 rounded-lg border ${
              data.perfuracao.readinessStatus === 'conforme' ? 'bg-emerald-50/50 border-emerald-200' :
              data.perfuracao.readinessStatus === 'acompanhamento' ? 'bg-blue-50/50 border-blue-200' :
              'bg-amber-50/50 border-amber-200'
            }`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <StatusBadge status={data.perfuracao.readinessStatus} language={language} />
              </div>
              <p className="text-[10px] text-slate-600 leading-relaxed">
                {data.perfuracao.readinessComment[language]}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ============ BLOCO 4: PONTE OPERAÇÃO → IMPACTO FINANCEIRO - McKinsey Premium Compact ============ */}
      <div className="bg-gradient-to-br from-[#0A192F] to-[#112240] rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gold-500/20 flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-gold-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{language === 'pt' ? 'Ponte Operação → Impacto Financeiro' : 'Operation → Financial Impact Bridge'}</h3>
            <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Leitura qualitativa do desvio operacional' : 'Qualitative reading of operational deviation'}</p>
          </div>
        </div>
        
        {/* Compact Table Format */}
        <div className="overflow-hidden rounded-lg border border-white/10">
          <table className="w-full">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Desvio' : 'Deviation'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Implicação Operacional' : 'Operational Implication'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Leitura Financeira' : 'Financial Reading'}</th>
                <th className="text-center py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-20">{language === 'pt' ? 'Sev.' : 'Sev.'}</th>
              </tr>
            </thead>
            <tbody>
              {data.ponteFinanceira.map((item, idx) => (
                <tr key={idx} className={`border-t border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs font-medium ${
                      item.severidade === 'low' ? 'text-emerald-400' :
                      item.severidade === 'medium' ? 'text-amber-400' :
                      'text-rose-400'
                    }`}>
                      {item.desvio[language]}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.implicacaoOperacional[language]}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.leituraFinanceira[language]}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      item.severidade === 'low' ? 'bg-emerald-500/20 text-emerald-400' :
                      item.severidade === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-rose-500/20 text-rose-400'
                    }`}>
                      {item.severidade === 'low' ? (language === 'pt' ? 'Baixo' : 'Low') :
                       item.severidade === 'medium' ? (language === 'pt' ? 'Médio' : 'Med') :
                       (language === 'pt' ? 'Alto' : 'High')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 mt-3 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {language === 'pt' ? 'Nota: Leitura qualitativa — quantificação financeira requer análise dedicada' : 'Note: Qualitative reading — financial quantification requires dedicated analysis'}
        </p>
      </div>

      {/* ============ BLOCO 5: WATCHLIST DE RISCO MINEIRO - McKinsey Premium Compact ============ */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center">
            <Shield className="w-4 h-4 text-rose-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Watchlist de Risco Mineiro' : 'Mine Risk Watchlist'}</h3>
            <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Riscos identificados nos relatórios mensais e POA' : 'Risks identified in monthly reports and POA'}</p>
          </div>
        </div>
        
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left py-2 px-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Risco' : 'Risk'}</th>
                <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-16">Sev.</th>
                <th className="text-left py-2 px-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Evidência' : 'Evidence'}</th>
                <th className="text-left py-2 px-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Impl. Operacional' : 'Op. Implication'}</th>
                <th className="text-left py-2 px-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Impl. Financeira' : 'Fin. Implication'}</th>
                <th className="text-left py-2 px-2.5 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Mitigação' : 'Mitigation'}</th>
              </tr>
            </thead>
            <tbody>
              {data.watchlist.map((item, idx) => (
                <tr key={idx} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/30' : ''} hover:bg-slate-50/60 transition-colors`}>
                  <td className="py-2 px-2.5">
                    <div className="flex items-center gap-1.5">
                      <AlertTriangle className={`w-3 h-3 flex-shrink-0 ${
                        item.severity === 'high' ? 'text-rose-500' :
                        item.severity === 'medium' ? 'text-amber-500' : 'text-emerald-500'
                      }`} />
                      <span className="font-medium text-slate-700 text-[11px] leading-tight">{item.risk[language]}</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${
                      item.severity === 'high' ? 'bg-rose-100 text-rose-700' :
                      item.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {item.severity === 'high' ? (language === 'pt' ? 'Alto' : 'High') :
                       item.severity === 'medium' ? (language === 'pt' ? 'Méd' : 'Med') :
                       (language === 'pt' ? 'Baixo' : 'Low')}
                    </span>
                  </td>
                  <td className="py-2 px-2.5">
                    <span className="text-slate-600 text-[10px] leading-tight">{item.evidence[language]}</span>
                  </td>
                  <td className="py-2 px-2.5">
                    <span className="text-slate-600 text-[10px] leading-tight">{item.implicacaoOperacional[language]}</span>
                  </td>
                  <td className="py-2 px-2.5">
                    <span className="text-slate-600 text-[10px] leading-tight">{item.implicacaoFinanceira[language]}</span>
                  </td>
                  <td className="py-2 px-2.5">
                    <span className="text-slate-600 text-[10px] leading-tight">{item.mitigation[language]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 mt-2.5 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção, POA 2026 e reuniões de acompanhamento operacional' : 'Source: Monthly Production Reports, POA 2026 and operational follow-up meetings'}
        </p>
      </div>
    </div>
  );
};

export default MineAccess;
